import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Loader2, Receipt } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";

const CATEGORIES = ["operations", "supplies", "rent", "utilities", "salary", "marketing", "other"];
const empty = { title: "", category: "operations", amount: 0, vendor: "", date: new Date().toISOString().slice(0, 10), notes: "" };
const peso = (n) => `₱${(n || 0).toLocaleString("en-PH", { minimumFractionDigits: 2 })}`;

const ExpensesPage = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const [filter, setFilter] = useState("");

  const fetchAll = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(filter ? `/expenses?category=${filter}` : "/expenses");
      setItems(data);
    } catch { toast.error("Failed to load expenses"); }
    finally { setLoading(false); }
  };
  useEffect(() => { fetchAll(); /* eslint-disable-line */ }, [filter]);

  const total = items.reduce((a, b) => a + (b.amount || 0), 0);

  const openCreate = () => { setEditing(null); setForm(empty); setOpen(true); };
  const openEdit = (x) => { setEditing(x); setForm({ ...empty, ...x }); setOpen(true); };

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...form, amount: parseFloat(form.amount) || 0 };
      if (editing) await api.patch(`/expenses/${editing.expense_id}`, payload);
      else await api.post("/expenses", payload);
      toast.success("Saved");
      setOpen(false); fetchAll();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setSaving(false); }
  };

  const remove = async (x) => {
    if (!window.confirm("Delete this expense?")) return;
    try { await api.delete(`/expenses/${x.expense_id}`); toast.success("Deleted"); fetchAll(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="p-6 lg:p-10 pav-fade-in" data-testid="expenses-page">
      <div className="flex flex-wrap items-end justify-between gap-3 mb-6">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-[#06B6D4] font-semibold">Accounting</p>
          <h1 className="font-display text-3xl font-extrabold text-[#0B1E3F] mt-1">Expenses</h1>
          <p className="text-sm text-neutral-500 mt-1">Total in view: <span className="font-bold text-[#0B1E3F]">{peso(total)}</span></p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={filter || "all"} onValueChange={(v) => setFilter(v === "all" ? "" : v)}>
            <SelectTrigger className="pav-input min-w-[150px]" data-testid="expenses-category-filter"><SelectValue placeholder="All categories" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All categories</SelectItem>
              {CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
            </SelectContent>
          </Select>
          <button onClick={openCreate} className="btn-accent inline-flex items-center gap-2" data-testid="expense-create-button">
            <Plus className="h-4 w-4" /> Add expense
          </button>
        </div>
      </div>

      <div className="pav-card p-0 overflow-hidden">
        {loading ? (
          <div className="p-12 text-center"><Loader2 className="h-6 w-6 animate-spin mx-auto" /></div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-[#F4F6FB] text-xs uppercase tracking-wider text-neutral-600">
                <tr>
                  <th className="text-left px-4 py-3">Date</th>
                  <th className="text-left px-4 py-3">Title</th>
                  <th className="text-left px-4 py-3">Category</th>
                  <th className="text-left px-4 py-3">Vendor</th>
                  <th className="text-right px-4 py-3">Amount</th>
                  <th className="text-right px-4 py-3">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#E2E8F0] text-sm">
                {items.map((x) => (
                  <tr key={x.expense_id} data-testid={`expense-row-${x.expense_id}`}>
                    <td className="px-4 py-3 text-neutral-600">{x.date}</td>
                    <td className="px-4 py-3 font-semibold">
                      {x.title}
                      {x.auto_generated && <span className="ml-2 text-[10px] uppercase text-[#06B6D4]">auto</span>}
                    </td>
                    <td className="px-4 py-3"><span className="pav-chip capitalize">{x.category}</span></td>
                    <td className="px-4 py-3 text-neutral-600">{x.vendor || "—"}</td>
                    <td className="px-4 py-3 text-right font-display font-bold text-[#0B1E3F]">{peso(x.amount)}</td>
                    <td className="px-4 py-3 text-right">
                      <button onClick={() => openEdit(x)} data-testid={`expense-edit-${x.expense_id}`} className="p-1.5 hover:bg-[#F4F6FB] rounded mr-1">
                        <Pencil className="h-4 w-4 text-[#0B1E3F]" />
                      </button>
                      <button onClick={() => remove(x)} data-testid={`expense-delete-${x.expense_id}`} className="p-1.5 hover:bg-red-50 rounded">
                        <Trash2 className="h-4 w-4 text-red-600" />
                      </button>
                    </td>
                  </tr>
                ))}
                {items.length === 0 && (
                  <tr><td colSpan="6" className="px-4 py-10 text-center text-neutral-500">
                    <Receipt className="h-8 w-8 mx-auto mb-2 text-neutral-300" />
                    No expenses recorded.
                  </td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md" data-testid="expense-dialog">
          <DialogHeader><DialogTitle>{editing ? "Edit expense" : "New expense"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-xs uppercase tracking-wider font-semibold text-neutral-600">Title</label>
              <input className="pav-input mt-1" data-testid="expense-title-input"
                value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs uppercase tracking-wider font-semibold text-neutral-600">Category</label>
                <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                  <SelectTrigger className="pav-input mt-1" data-testid="expense-category-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs uppercase tracking-wider font-semibold text-neutral-600">Date</label>
                <input type="date" className="pav-input mt-1" data-testid="expense-date-input"
                  value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs uppercase tracking-wider font-semibold text-neutral-600">Amount ₱</label>
                <input type="number" min="0" step="0.01" className="pav-input mt-1"
                  data-testid="expense-amount-input"
                  value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} />
              </div>
              <div>
                <label className="text-xs uppercase tracking-wider font-semibold text-neutral-600">Vendor</label>
                <input className="pav-input mt-1" data-testid="expense-vendor-input"
                  value={form.vendor} onChange={(e) => setForm({ ...form, vendor: e.target.value })} />
              </div>
            </div>
            <div>
              <label className="text-xs uppercase tracking-wider font-semibold text-neutral-600">Notes</label>
              <textarea className="pav-input mt-1 min-h-[60px]" data-testid="expense-notes-input"
                value={form.notes || ""} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
            </div>
          </div>
          <DialogFooter>
            <button onClick={() => setOpen(false)} className="btn-outline text-sm">Cancel</button>
            <button onClick={save} disabled={saving} className="btn-primary text-sm inline-flex items-center gap-2 disabled:opacity-50" data-testid="expense-save-button">
              {saving && <Loader2 className="h-4 w-4 animate-spin" />} Save
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ExpensesPage;
